import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService, GenericError } from '@auth0/auth0-angular';
import { catchError, firstValueFrom, of, take, timeout } from 'rxjs';
import { AuthCallbackConstants, GuardConstants, SystemPageRoutes } from '../constants/constants';
import ErrorHandling from '../errors/error-handler';

/**
 * Stands on the route auth0-angular sends a failed sign-in callback to (errorPath in app.config.ts), and
 * picks the system page that explains it.
 *
 * Without it the SDK falls back to "/", where AuthGuard starts a fresh login, so a deactivated user - who
 * Auth0 refuses because the Authentication API blocked them - would be bounced back to the login form with
 * no explanation.
 */
export const authCallbackErrorGuard: CanActivateFn = async (_route, _state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // error$ replays the last error, so it is still there by the time this guard runs.
  const error = await firstValueFrom(authService.error$.pipe(
    take(1),
    timeout(GuardConstants.AUTH_CALLBACK_ERROR_TIMEOUT_MS()),
    catchError(() => of(undefined))
  ));

  if (isBlockedUserError(error)) {
    return router.createUrlTree(SystemPageRoutes.ACCOUNT_DEACTIVATED());
  }

  // Through the app's error handler rather than the console alone, so a failed sign-in reaches App Insights.
  // A blocked user above is an expected outcome, not an error, and is deliberately not reported.
  ErrorHandling.handleError(error ?? new Error('authCallbackErrorGuard: Sign-in callback failed without an error'));
  return router.createUrlTree(SystemPageRoutes.AUTH_ERROR());
};

function isBlockedUserError(error: Error | undefined): boolean {
  const callbackError = error as Partial<GenericError> | undefined;

  return callbackError?.error === AuthCallbackConstants.BLOCKED_ERROR()
    && callbackError?.error_description === AuthCallbackConstants.BLOCKED_ERROR_DESCRIPTION();
}
